import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, IconButton } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faHeart } from '@fortawesome/free-solid-svg-icons';

import { BookmarkIcon } from '../BookmarkIcon';
import { getFormatViews, getGameIcon } from '../../helpers';
import { Video } from '../../types';
import styles from './style';

const CLOUDFRONT_URL = process.env.REACT_APP_AWS_CLOUDFRONT_URL;

interface FeedActionsProps {
  video: Video;
  // The connected user posted the video: no "+" to follow.
  isOwn: boolean;
  commentsOpen: boolean;
  // Shown under the share button for a moment after the link is copied.
  shareLabel: string;
  onOpenUser: () => void;
  onFollow: () => void;
  onLike: () => void;
  onToggleComments: () => void;
  onFavorite: () => void;
  onShare: () => void;
}

// Column on the right of the player, like the app and TikTok: the creator with "+" to follow him,
// then the icons (heart, comment, favorite, share) and the game of the video at the bottom.
const FeedActions: React.FC<FeedActionsProps> = React.memo((props) => {
  const {
    video,
    isOwn,
    commentsOpen,
    shareLabel,
    onOpenUser,
    onFollow,
    onLike,
    onToggleComments,
    onFavorite,
    onShare,
  } = props;
  const { t } = useTranslation();

  const avatarUrl = useMemo(() => (video.user && video.user.avatar ? `${CLOUDFRONT_URL}${video.user.avatar}` : ''), [video.user]);
  const canFollow = !isOwn && !video.isFollowed;

  const gameIconUrl = useMemo(() => (video.gameName ? getGameIcon(video.gameName) : ''), [video.gameName]);
  // Remembers the url that failed, so the next video's game is tried again.
  const [failedGameIconUrl, setFailedGameIconUrl] = useState('');
  const onGameIconError = useCallback(() => {
    setFailedGameIconUrl(gameIconUrl);
  }, [gameIconUrl]);
  const showGameIcon = gameIconUrl !== '' && gameIconUrl !== failedGameIconUrl;

  return (
    <Box sx={styles.column}>
      <Box sx={styles.creator}>
        <IconButton disableRipple onClick={onOpenUser} aria-label={video.user ? video.user.username : ''} sx={styles.avatarButton}>
          <Avatar alt="" src={avatarUrl} sx={styles.avatar} />
        </IconButton>
        {canFollow && (
          <IconButton disableRipple onClick={onFollow} aria-label={t('admin.feed.follow')} sx={styles.follow}>
            <Add />
          </IconButton>
        )}
      </Box>
      <Box sx={styles.action}>
        <IconButton disableRipple onClick={onLike} aria-label={t('admin.feed.like')} sx={video.isLiked ? styles.buttonLiked : styles.button}>
          <FontAwesomeIcon icon={faHeart} />
        </IconButton>
        {getFormatViews(video.likes || 0)}
      </Box>
      <Box sx={styles.action}>
        <IconButton disableRipple onClick={onToggleComments} aria-label={t('admin.feed.comments')} sx={commentsOpen ? styles.buttonActive : styles.button}>
          <FontAwesomeIcon icon={faCommentDots} />
        </IconButton>
        {getFormatViews(video.comments || 0)}
      </Box>
      <Box sx={styles.action}>
        <IconButton disableRipple onClick={onFavorite} aria-label={t('admin.feed.favorite')} sx={video.isFavorite ? styles.buttonFavorite : styles.button}>
          <BookmarkIcon sx={styles.bookmarkIcon} />
        </IconButton>
        {getFormatViews(video.favorites || 0)}
      </Box>
      <Box sx={styles.action}>
        <IconButton disableRipple onClick={onShare} aria-label={t('admin.feed.share')} sx={styles.button}>
          {/* Same arrow as the app (Ionicons arrow-redo), viewBox cut to the glyph: same height as the heart. */}
          <svg viewBox="48 76 416 372" width="0.98em" height="0.875em" fill="currentColor" aria-hidden="true">
            <path d="M58.79 439.13A16 16 0 0 1 48 424c0-73.1 14.68-131.56 43.65-173.77 35-51 90.21-78.46 164.35-81.87V88a16 16 0 0 1 27.05-11.57l176 168a16 16 0 0 1 0 23.14l-176 168A16 16 0 0 1 256 424v-79.77c-45 1.36-79 8.65-106.07 22.64-29.25 15.12-50.46 37.71-73.32 67a16 16 0 0 1-17.82 5.28z" />
          </svg>
        </IconButton>
        {shareLabel || getFormatViews(video.shares || 0)}
      </Box>
      {showGameIcon && (
        <Box component="img" src={gameIconUrl} alt={video.gameName} title={video.gameName} decoding="async" onError={onGameIconError} sx={styles.gameIcon} />
      )}
    </Box>
  );
});

export default FeedActions;
